import React, { useEffect, useState } from 'react';
import { getCrmDispositions, initiateCrmCall, logCrmDisposition } from '../config/api';
import formatCallDuration from '../utils/formatCallDuration';

/**
 * CrmCallDialog — the click-to-call modal (spec Figure A4).
 *
 * Flow:
 *  1. On open, shows the bridge message + up to 3 recent calls for context.
 *  2. "Start call" calls the initiate endpoint (rings the agent's registered
 *     mobile, then bridges the contact — the number is never shown here).
 *  3. After the call the agent records a disposition, follow-up date and
 *     comment, which is saved against the created call log.
 *
 * Props:
 *  - leadId          (required)
 *  - contact         { id, name, phone } — the contact being called
 *  - hubNumber       optional hub virtual number string
 *  - recentCalls     array of recent call events (from the lead timeline)
 *  - onClose()       close the dialog
 *  - onLogged()      called after a disposition is saved (parent reloads)
 */
const DEFAULT_DISPOSITION_OPTIONS = [
  { value: 'connected_interested', label: 'Connected and interested' },
  { value: 'connected_not_interested', label: 'Connected and not interested' },
  { value: 'buys_elsewhere', label: 'Buys elsewhere' },
  { value: 'call_back_later', label: 'Call back later' },
  { value: 'did_not_answer', label: 'Did not answer' },
  { value: 'switched_off', label: 'Switched off' },
  { value: 'not_reachable', label: 'Not reachable' },
  { value: 'wrong_number', label: 'Wrong number' },
];
const CALL_STATUS_LABELS = {
  initiated: 'Initiated',
  answered_by_agent: 'Agent answered',
  answered_by_customer: 'Customer answered',
  answered: 'Connected',
  connected: 'Connected',
  no_answer: 'No answer',
  missed: 'Missed',
};
const UNASSIGNED_HUB_VALUE = 'unassigned';

const CrmCallDialog = ({
  leadId,
  contact,
  hubNumber,
  hubLinks = [],
  preferredHubId,
  orderId,
  recentCalls = [],
  onClose,
  onLogged,
}) => {
  const [starting, setStarting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [callMode, setCallMode] = useState('mobile');
  // Which hub to attribute this call to (for the dashboard). Auto-selected when
  // the lead has a single hub; a picker is shown when it has several.
  const [hubId, setHubId] = useState(() => {
    const preferredHub = hubLinks.find(
      (link) => Number(link.hubId) === Number(preferredHubId)
    );
    if (preferredHub) return String(preferredHub.hubId);
    if (hubLinks.length === 1) return String(hubLinks[0].hubId);
    return hubLinks.length === 0 ? UNASSIGNED_HUB_VALUE : '';
  });
  const [callId, setCallId] = useState(null);
  const [bridged, setBridged] = useState(false);
  const [disposition, setDisposition] = useState('connected_interested');
  const [dispositionOptions, setDispositionOptions] = useState(DEFAULT_DISPOSITION_OPTIONS);
  const [followUpDate, setFollowUpDate] = useState('');
  const [comment, setComment] = useState('');

  useEffect(() => {
    let active = true;
    getCrmDispositions()
      .then((response) => {
        const rows = response?.data?.dispositions || [];
        if (!active || !rows.length) return;
        const options = rows.map((row) => ({ value: row.key, label: row.label }));
        setDispositionOptions(options);
        setDisposition((current) =>
          options.some((option) => option.value === current) ? current : options[0].value
        );
      })
      .catch(() => {
        // Keep the established list if settings cannot be loaded during a call.
      });
    return () => { active = false; };
  }, []);

  const startCall = async () => {
    if (!hubId) {
      alert('Select a Hub before starting the call.');
      return;
    }
    setStarting(true);
    try {
      const res = await initiateCrmCall(leadId, {
        contactId: contact?.id,
        hubId: hubId === UNASSIGNED_HUB_VALUE ? null : Number(hubId),
        hubNumber,
        provider: 'smartflo',
        mode: callMode,
        orderId,
      });
      const data = res?.data || res;
      if (data && data.success && data.callId) {
        setCallId(data.callId);
        setBridged(true);
      } else {
        alert(data?.providerError || res?.message || 'Could not start the call.');
      }
    } catch (err) {
      alert(err.message || 'Could not start the call.');
    } finally {
      setStarting(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (callId) {
        await logCrmDisposition(leadId, callId, {
          disposition,
          comment: comment || undefined,
          followUpDate: followUpDate || undefined,
        });
      }
      if (onLogged) onLogged();
      onClose();
    } catch (err) {
      alert(err.message || 'Could not save the disposition.');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (d) => (
    d ? new Date(d).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }) : ''
  );
  const last3 = recentCalls.slice(0, 3);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content crm-call-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header crm-call-modal-header">
          <div className="crm-call-heading">
            <span className="crm-call-heading-icon" aria-hidden="true">☎</span>
            <div>
              <div className="crm-call-eyebrow">Smartflo call</div>
              <h3>Call {contact?.name || 'contact'}</h3>
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close call dialog">×</button>
        </div>

        <div className="modal-body">
          <div className="crm-call-bridge">
            <span className="crm-call-bridge-icon" aria-hidden="true">i</span>
            <span>{bridged
              ? callMode === 'mobile'
                ? 'Smartflo accepted the call. Your registered mobile will ring first, then Smartflo will bridge the contact.'
                : 'Smartflo accepted the call on your desktop extension and will bridge the contact.'
              : callMode === 'mobile'
                ? 'Smartflo will ring your registered mobile first, then bridge the contact. The customer number stays masked.'
                : 'Smartflo will ring your desktop extension first, then bridge the contact. The customer number stays masked.'}</span>
          </div>

          {!bridged && (
            <div className="form-group">
              <label className="form-label">Call using</label>
              <div className="crm-call-mode-options" role="radiogroup" aria-label="Call using">
                <button
                  type="button"
                  role="radio"
                  aria-checked={callMode === 'mobile'}
                  className={`crm-call-mode-option${callMode === 'mobile' ? ' is-active' : ''}`}
                  disabled={starting}
                  onClick={() => setCallMode('mobile')}
                >
                  <span className="crm-call-mode-icon" aria-hidden="true">☎</span>
                  <span><strong>Phone</strong><small>Registered mobile</small></span>
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={callMode === 'extension'}
                  className={`crm-call-mode-option${callMode === 'extension' ? ' is-active' : ''}`}
                  disabled={starting}
                  onClick={() => setCallMode('extension')}
                >
                  <span className="crm-call-mode-icon" aria-hidden="true">▣</span>
                  <span><strong>Desktop</strong><small>Smartflo extension</small></span>
                </button>
              </div>
            </div>
          )}

          {!bridged && (
            <div className="form-group">
              <label className="form-label" htmlFor="crm-call-hub">Attribute to hub *</label>
              <select
                id="crm-call-hub"
                className="form-control"
                value={hubId}
                disabled={starting}
                onChange={(e) => setHubId(e.target.value)}
                required
              >
                <option value="" disabled>
                  Select Hub
                </option>
                {hubLinks.length === 0 && (
                  <option value={UNASSIGNED_HUB_VALUE}>Unassigned</option>
                )}
                {hubLinks.map((h) => (
                  <option key={h.id} value={h.hubId}>
                    {[h.name, h.city].filter(Boolean).join(' · ') || 'Unnamed Hub'}
                  </option>
                ))}
              </select>
              {hubLinks.length === 0 && (
                <div className="crm-muted" style={{ marginTop: '6px' }}>
                  No Hub is linked. This call will be recorded as Unassigned.
                </div>
              )}
            </div>
          )}

          {last3.length > 0 && (
            <div className="crm-call-recent">
              <div className="crm-call-recent-heading">
                <span>Recent calls</span>
                <span className="crm-call-recent-count">Last {last3.length}</span>
              </div>
              {last3.map((c) => {
                const d = c.data || {};
                const statusLabel = CALL_STATUS_LABELS[d.providerStatus];
                return (
                  <div className="crm-call-recent-item" key={c.id}>
                    <div className="crm-call-recent-topline">
                      <span className="crm-call-recent-date">{fmt(c.timestamp)}</span>
                      <span className={`crm-call-recent-status crm-call-recent-status-${d.providerStatus || 'default'}`}>
                        {statusLabel || d.dispositionLabel || d.disposition || 'Call'}
                      </span>
                      {['answered', 'connected'].includes(d.providerStatus) && d.duration > 0 && (
                        <span className="crm-call-recent-duration">{formatCallDuration(d.duration, d.durationSource === 'estimated')}</span>
                      )}
                    </div>
                    {d.comment && <div className="crm-call-recent-comment">{d.comment}</div>}
                  </div>
                );
              })}
            </div>
          )}

          {bridged && (
            <>
              <div className="form-group">
                <label className="form-label">Disposition</label>
                <select
                  className="form-control"
                  value={disposition}
                  onChange={(e) => setDisposition(e.target.value)}
                >
                  {dispositionOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Follow-up date</label>
                <input
                  type="date"
                  className="form-control"
                  value={followUpDate}
                  onChange={(e) => setFollowUpDate(e.target.value)}
                />
              </div>

              <div className="form-group">
                <label className="form-label">Comment</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="What was said, what is next"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          {!bridged ? (
            <button className="btn btn-success crm-call-start-button" disabled={starting || !hubId} onClick={startCall}>
              <span aria-hidden="true">☎</span>
              {starting ? 'Starting…' : 'Start call'}
            </button>
          ) : (
            <button className="btn btn-primary" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'End call and save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CrmCallDialog;
